import React, { useEffect, useMemo, useRef, useState } from 'react';
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Download, ZoomIn, ZoomOut, Maximize2 } from 'lucide-react';
import { downloadText } from '@/lib/exporters';
import { toast } from 'sonner';

export default function PanelSheetPreview({ open, onOpenChange, svg, meta, filename }) {
  const [zoom, setZoom] = useState(0.4);
  const wrapRef = useRef(null);

  // Reset zoom whenever a new SVG comes in.
  useEffect(() => {
    if (open) setZoom(0.4);
  }, [open, svg]);

  const svgDataUrl = useMemo(() => {
    if (!svg) return '';
    return `data:image/svg+xml;charset=utf-8,${encodeURIComponent(svg)}`;
  }, [svg]);

  const handleDownload = () => {
    downloadText(filename, svg, 'image/svg+xml');
    toast.success('Panel sheet downloaded');
  };

  return (
    <Dialog open={open} onOpenChange={onOpenChange}>
      <DialogContent className="max-w-5xl w-[95vw] rounded-2xl p-0 overflow-hidden" data-testid="panel-preview-dialog">
        <DialogHeader className="px-6 pt-6 pb-3 border-b border-slate-200">
          <DialogTitle className="text-lg tracking-tight">Panel sheet preview</DialogTitle>
          <DialogDescription>
            {meta ? (
              <span data-testid="panel-preview-meta">
                {meta.panels} panels · sheet {meta.sheetWidthMM}×{Math.round(meta.sheetHeightMM)}mm
                {meta.spherized ? ' · spherized (curls into a round ball when stitched)' : ''} · each edge is labelled with the neighbouring panel number so you know which piece it stitches to.
              </span>
            ) : (
              'Loading…'
            )}
          </DialogDescription>
        </DialogHeader>

        <div className="flex items-center gap-2 px-4 py-2 border-b border-slate-200 bg-slate-50/60">
          <Button
            variant="ghost"
            size="icon"
            className="rounded-full h-8 w-8"
            onClick={() => setZoom((z) => Math.max(0.15, z * 0.8))}
            data-testid="btn-preview-zoom-out"
          >
            <ZoomOut className="w-4 h-4" />
          </Button>
          <span className="font-mono text-xs text-slate-600 w-12 text-center" data-testid="preview-zoom-value">
            {Math.round(zoom * 100)}%
          </span>
          <Button
            variant="ghost"
            size="icon"
            className="rounded-full h-8 w-8"
            onClick={() => setZoom((z) => Math.min(4, z * 1.25))}
            data-testid="btn-preview-zoom-in"
          >
            <ZoomIn className="w-4 h-4" />
          </Button>
          <Button
            variant="ghost"
            size="icon"
            className="rounded-full h-8 w-8"
            onClick={() => setZoom(1)}
            data-testid="btn-preview-actual"
            title="Actual size (1mm = 1px)"
          >
            <Maximize2 className="w-4 h-4" />
          </Button>
          <div className="ml-auto flex items-center gap-2">
            <Button
              className="rounded-full h-9 bg-[#007AFF] hover:bg-[#0062CC] text-white"
              onClick={handleDownload}
              data-testid="btn-preview-download"
            >
              <Download className="w-4 h-4 mr-2" />
              Download SVG
            </Button>
          </div>
        </div>

        <div
          ref={wrapRef}
          className="overflow-auto bg-[#F1F3F5] p-6 max-h-[70vh]"
          data-testid="panel-preview-scroll"
        >
          {svgDataUrl ? (
            <div
              className="mx-auto bg-white shadow-md"
              style={{
                width: `${(meta?.sheetWidthMM || 300) * zoom}px`,
                height: `${(meta?.sheetHeightMM || 400) * zoom}px`,
              }}
            >
              <img
                src={svgDataUrl}
                alt="Panel sheet"
                data-testid="panel-preview-image"
                style={{ width: '100%', height: '100%', display: 'block' }}
              />
            </div>
          ) : (
            <div className="text-center text-slate-500 py-16">No preview available.</div>
          )}
        </div>
      </DialogContent>
    </Dialog>
  );
}
